import {Component, EventEmitter, Input, Output} from '@angular/core';
import {AuthService, UserType} from '@app/core/services/auth.service';
import {User} from '@app/core/models/user.model';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {FileEntry} from '@app/core/models/fileEntry.model';
import {EQOColor, EQOIcon, EQORebrandColor, EQORebrandIcon} from '@quickorder/angular-web-lib';
import {FileEntryService} from '@app/modules/shared/services/fileEntry.service';
import {HttpService} from '@app/core/services/http.service';
import {DateService} from '@app/core/services/date.service';
import {MatDialog} from '@angular/material/dialog';
import {MediaLibraryComponent} from '@app/modules/shared/components/media-library/media-library.component';
import {take} from 'rxjs/operators';

@Component({
	selector: 'file-entries',
	templateUrl: './file-entries.component.html',
	styleUrls: ['./file-entries.component.scss'],
})
export class FileEntriesComponent {

	public ICON = EQOIcon;
	public COLOR = EQOColor;
	public EQORebrandIcon: typeof EQORebrandIcon = EQORebrandIcon;
	public EQORebrandColor: typeof EQORebrandColor = EQORebrandColor;
	public ACCEPT = [
		'.ai',
		'.avi',
		'.bmp',
		'.csv',
		'.doc',
		'.docx',
		'.flv',
		'.gif',
		'.h264',
		'.ico',
		'.jpeg',
		'.jpg',
		'.key',
		'.mov',
		'.mp4',
		'.mpeg',
		'.mpg',
		'.odp',
		'.ods',
		'.odt',
		'.pdf',
		'.png',
		'.pps',
		'.pptx',
		'.ps',
		'.psd',
		'.rar',
		'.rm',
		'.rtf',
		'.svg',
		'.swf',
		'.tif',
		'.tiff',
		'.txt',
		'.vob',
		'.wks',
		'.wmv',
		'.wpd',
		'.wps',
		'.xlr',
		'.xls',
		'.xlsx',
		'.yz',
		'.zip',
	];

	@Input()
	public set fileEntries(fileEntries: FileEntry[]) {
		this.currentFileEntries = [];

		if (fileEntries) {
			fileEntries.forEach((fileEntry: FileEntry) => {
				this.currentFileEntries.push(fileEntry);
			});
			this.emitFileIds();
		}
	}

	@Input() public withFileSelector: boolean = false;
	@Input() public edit: boolean = true;
	@Input() public withFileSelection: boolean = false;
	@Input() public selectedFiles: FileEntry[] = [];
	@Output() public fileIds: EventEmitter<number[]> = new EventEmitter<number[]>();
	@Output() public addedFileEntries: EventEmitter<FileEntry[]> = new EventEmitter<FileEntry[]>();
	@Output() public fileSelected: EventEmitter<FileEntry> = new EventEmitter<FileEntry>();
	@Output() public fileDeselected: EventEmitter<FileEntry> = new EventEmitter<FileEntry>();

	public currentFileEntries: FileEntry[] = [];
	public currentUser: User;

	public filesToUpload: File[] = [];
	public newFiles: FileEntry[] = [];
	public showLoader: boolean = false;

	constructor(
		public authService: AuthService,
		private fileEntryService: FileEntryService,
		private store: Store<IAppState>,
		public httpService: HttpService,
		private dateService: DateService,
		private dialog: MatDialog,
	) {
		this.currentUser = this.authService.currentUser;

		this.currentFileEntries = this.fileEntries;
	}

	public removeFileEntry(fileEntry: FileEntry): void {
		this.currentFileEntries = this.currentFileEntries.filter(f => f.id !== fileEntry.id);
		this.deselectFile(fileEntry);
		this.emitFileIds();
	}

	public async filesAdded(event): Promise<void> {
		const files = event.target.files;
		this.filesToUpload = [];
		this.newFiles = [];
		for (const key in files) {
			if (!files.hasOwnProperty(key)) {
				continue;
			}
			const fullFileName = files[key].name;
			const fileExtension = fullFileName.split('.').pop();
			if (files[key].size > 100000000) {
				alert(this.httpService.translate.instant('FILE_ENTRY_MANAGER.X_FILE_TOO_LARGE', {x: files[key].name}));
				// tslint:disable-next-line:max-line-length
			} else if (this.ACCEPT.indexOf('.' + fileExtension.toLowerCase()) === -1) {
				alert(this.httpService.translate.instant('FILE_ENTRY_MANAGER.X_FILE_NOT_ACCEPTED_FORMAT', {x: files[key].name}));
			} else {
				const file = files[key];
				this.filesToUpload.push(file);
			}
		}
		this.showLoader = true;
		const fileEntries = await this.fileEntryService.uploadFiles(this.filesToUpload).pipe(take(1)).toPromise();
		this.newFiles = fileEntries;
		this.addedFileEntries.emit(this.newFiles);
		this.showLoader = false;
		fileEntries.forEach(entry => this.currentFileEntries.unshift(entry));
		if (this.withFileSelector) {
			this.emitFileIds();
		}
	}

	public downloadFile(fileEntry: FileEntry): void {
		if (this.authService.userType === UserType.USER) {
			this.fileEntryService.downloadFile(fileEntry.id);
		} else {
			this.fileEntryService.downloadFilePunchUser(fileEntry.id);
		}
	}

	public openMediaLibrary(): void {
		this.dialog.open(MediaLibraryComponent, {
			panelClass: 'fullscreen-dialog-max-s',
		}).afterClosed().pipe(take(1)).subscribe((files: FileEntry[]) => {
			if (!files) {
				return;
			}
			this.emitFileIds();
			this.addedFileEntries.emit(files);
		});
	}

	public emitFileIds(): void {
		const ids = [...new Set(this.currentFileEntries.map((value) => value.id))];
		this.fileIds.emit(ids);
	}

	public selectFile(file: FileEntry): void {
		this.fileSelected.emit(file);
	}

	public deselectFile(file: FileEntry): void {
		this.fileDeselected.emit(file);
	}

	public isFileSelected(file: FileEntry): boolean {
		return this.selectedFiles.find(f => f.id === file.id) !== undefined;
	}

	public ionViewDidEnter(): void {
		this.currentUser = this.authService.currentUser;
	}
}
